<template>
  <div class="seo-effect-report-page" v-loading="loading">
    <header class="report-toolbar">
      <div>
        <h1 class="report-toolbar__title">效果月报</h1>
        <div class="report-toolbar__subtitle">
          {{ reportSubtitle }}
        </div>
      </div>
      <div class="report-toolbar__actions">
        <el-select
          v-model="siteId"
          class="report-toolbar__site"
          size="small"
          filterable
          placeholder="选择站点"
          @change="onSiteChange"
        >
          <el-option
            v-for="site in siteOptions"
            :key="site.id"
            :label="`${site.name}（${site.url}）`"
            :value="site.id"
          />
        </el-select>
        <el-select
          v-model="statMonth"
          class="report-toolbar__month"
          size="small"
          placeholder="选择月份"
          @change="onMonthChange"
        >
          <el-option
            v-for="month in monthOptions"
            :key="month.stat_month"
            :label="month.label"
            :value="month.stat_month"
          >
            <span>{{ month.label }}</span>
            <span class="month-option__status">
              {{ monthStatusText(month.status) }}
            </span>
          </el-option>
        </el-select>
        <el-radio-group v-model="viewMode" size="small" @change="loadReport">
          <el-radio-button label="draft">草稿</el-radio-button>
          <el-radio-button label="published">已发布</el-radio-button>
        </el-radio-group>
        <el-tooltip content="刷新本地数据" placement="top">
          <el-button
            v-if="canEdit"
            size="small"
            icon="el-icon-refresh"
            :disabled="!reportState.id || viewMode !== 'draft'"
            @click="refreshLocal"
          />
        </el-tooltip>
        <el-button
          v-if="canEdit"
          size="small"
          icon="el-icon-edit"
          :disabled="!report || viewMode !== 'draft'"
          @click="openEditor"
        >
          编辑月报
        </el-button>
        <el-button
          v-if="canPublish"
          type="success"
          size="small"
          icon="el-icon-s-promotion"
          :disabled="!canPublishCurrent"
          @click="publishReport"
        >
          发布
        </el-button>
        <el-button
          v-if="canExport"
          type="primary"
          size="small"
          icon="el-icon-download"
          :disabled="!report"
          @click="exportReport"
        >
          导出
        </el-button>
      </div>
    </header>

    <div v-if="reportState.id" class="report-state-line">
      <div class="report-state-line__main">
        <el-tag :type="stateTagType" size="mini">
          {{ reportStateText }}
        </el-tag>
        <span v-if="reportState.has_unpublished_changes">
          当前草稿有未发布修改
        </span>
        <span>修订 {{ reportState.revision || 1 }}</span>
        <span v-if="reportState.published_version">
          发布版本 {{ reportState.published_version }}
        </span>
      </div>
      <div class="report-state-line__sources">
        <span
          v-for="(source, key) in sourceStatuses"
          :key="key"
          :class="['source-state', `source-state--${source.status}`]"
          :title="source.message"
        >
          {{ sourceName(key) }} {{ sourceStatusText(source.status) }}
        </span>
      </div>
    </div>

    <div v-if="report" class="report-print-area">
      <div class="report-print-head">
        <div>
          <h1>效果月报</h1>
          <p>{{ reportSubtitle }}</p>
        </div>
        <div class="report-print-head__version">
          {{ report.meta.month_label }}
        </div>
      </div>
      <div class="data-line">
        <div>
          数据来源：Google Search Console · Google Analytics · 表单线索 ·
          DataForSEO
        </div>
        <span> 数据更新于 {{ formatTime(report.meta.data_updated_at) }} </span>
      </div>
      <effect-report-content ref="reportContent" :report="report" />
    </div>

    <div v-else-if="!loading" class="report-empty">
      <i class="el-icon-document"></i>
      <span>{{ emptyState || "暂无月报数据" }}</span>
    </div>
  </div>
</template>

<script>
import EffectReportContent from "@/components/effect-report/EffectReportContent.vue";
import {
  apiSeoEffectReportDetail,
  apiSeoEffectReportExport,
  apiSeoEffectReportPublish,
  apiSeoEffectReportRefresh,
} from "@/api/seo";

export default {
  name: "SeoEffectReport",
  components: { EffectReportContent },
  data() {
    return {
      loading: false,
      siteId: "",
      statMonth: "",
      viewMode: "draft",
      siteOptions: [],
      monthOptions: [],
      report: null,
      reportState: {},
      sourceStatuses: {},
      emptyState: "",
    };
  },
  computed: {
    canEdit() {
      return this.$hasPermission("seo.effect_report", "edit");
    },
    canPublish() {
      return this.$hasPermission("seo.effect_report", "publish");
    },
    canExport() {
      return this.$hasPermission("seo.effect_report", "export");
    },
    canPublishCurrent() {
      return (
        this.viewMode === "draft" &&
        this.report &&
        ["ga4", "gsc"].every(
          (key) => (this.sourceStatuses[key] || {}).status === "ready"
        )
      );
    },
    reportSubtitle() {
      const meta = (this.report || {}).meta || {};
      if (meta.site_name || meta.site_url) {
        return `${meta.site_name || meta.site_url} · ${
          meta.month_label || this.statMonth
        }`;
      }
      const site = this.siteOptions.find((item) => item.id === this.siteId);
      return `${(site || {}).name || "请选择站点"} · ${
        this.statMonth || "请选择月份"
      }`;
    },
    reportStateText() {
      if (this.reportState.status === "published") return "已发布";
      return "草稿";
    },
    stateTagType() {
      return this.reportState.status === "published" ? "success" : "info";
    },
  },
  created() {
    const query = this.$route.query || {};
    const siteId = Number(query.site_id || 0);
    const statMonth = String(query.stat_month || "");
    if (siteId > 0) {
      this.siteId = siteId;
    }
    if (/^\d{4}-(?:0[1-9]|1[0-2])$/.test(statMonth)) {
      this.statMonth = statMonth;
    }
    this.loadReport();
  },
  methods: {
    loadReport() {
      this.loading = true;
      return apiSeoEffectReportDetail({
        site_id: this.siteId || undefined,
        stat_month: this.statMonth || undefined,
        view_mode: this.viewMode,
      })
        .then((response) => {
          this.applyResponse(response);
        })
        .finally(() => {
          this.loading = false;
        });
    },
    applyResponse(response) {
      const data = response || {};
      this.siteOptions = data.site_options || [];
      this.monthOptions = data.month_options || [];
      this.reportState = data.report_state || {};
      this.sourceStatuses = data.source_statuses || {};
      this.report = data.report || null;
      this.emptyState = data.empty_state || "";
      if (this.reportState.site_id) {
        this.siteId = Number(this.reportState.site_id);
      } else if (!this.siteId && this.siteOptions[0]) {
        this.siteId = Number(this.siteOptions[0].id);
      }
      if (this.reportState.stat_month) {
        this.statMonth = this.reportState.stat_month;
      } else if (!this.statMonth && this.monthOptions[0]) {
        this.statMonth = this.monthOptions[0].stat_month;
      }
      this.syncRoute();
    },
    onSiteChange() {
      this.statMonth = "";
      this.viewMode = "draft";
      this.loadReport();
    },
    onMonthChange() {
      this.loadReport();
    },
    refreshLocal() {
      this.$confirm("将使用当前本地数据重建月报草稿，是否继续？", "刷新草稿", {
        type: "info",
      }).then(() => {
        this.loading = true;
        apiSeoEffectReportRefresh(this.commonPayload())
          .then((response) => {
            this.applyResponse(response);
            this.$message.success("草稿已刷新");
          })
          .finally(() => {
            this.loading = false;
          });
      });
    },
    openEditor() {
      this.$router.push({
        path: "/seo/effect-report/edit",
        query: {
          site_id: String(this.siteId),
          stat_month: this.statMonth,
        },
      });
    },
    publishReport() {
      this.$confirm(
        "发布后用户端将读取本次固定快照，是否继续？",
        "发布效果月报",
        { type: "warning" }
      ).then(() => {
        this.loading = true;
        apiSeoEffectReportPublish(this.commonPayload())
          .then((response) => {
            this.viewMode = "published";
            this.applyResponse(response);
            this.$message.success("月报已发布");
          })
          .finally(() => {
            this.loading = false;
          });
      });
    },
    exportReport() {
      this.loading = true;
      apiSeoEffectReportExport({
        site_id: this.siteId,
        stat_month: this.statMonth,
        view_mode: this.viewMode,
      })
        .then((response) => {
          this.applyResponse(response);
          this.$nextTick(() => {
            if (this.$refs.reportContent) {
              this.$refs.reportContent.expandAll();
            }
            setTimeout(() => window.print(), 80);
          });
        })
        .finally(() => {
          this.loading = false;
        });
    },
    commonPayload() {
      return {
        site_id: this.siteId,
        stat_month: this.statMonth,
        revision: this.reportState.revision,
      };
    },
    syncRoute() {
      const query = {};
      if (this.siteId) query.site_id = String(this.siteId);
      if (this.statMonth) query.stat_month = this.statMonth;
      const current = this.$route.query || {};
      if (
        String(current.site_id || "") === String(query.site_id || "") &&
        String(current.stat_month || "") === String(query.stat_month || "")
      ) {
        return;
      }
      this.$router.replace({ path: this.$route.path, query }).catch(() => {});
    },
    sourceName(key) {
      return {
        ga4: "GA4",
        gsc: "GSC",
        clue: "线索",
        dataforseo: "DataForSEO",
        manual: "人工数据",
      }[key];
    },
    sourceStatusText(status) {
      return {
        ready: "完整",
        partial: "待补充",
        missing: "未配置",
        failed: "失败",
      }[status];
    },
    monthStatusText(status) {
      return {
        published: "已发布",
        draft: "草稿",
        uncreated: "未创建",
      }[status];
    },
    formatTime(timestamp) {
      if (!timestamp) return "—";
      return new Date(Number(timestamp) * 1000).toLocaleString("zh-CN", {
        hour12: false,
      });
    },
  },
};
</script>

<style lang="scss" scoped>
.seo-effect-report-page {
  width: 100%;
  max-width: 1680px;
  min-height: calc(100vh - 72px);
  margin: 0 auto;
  padding: 16px;
  box-sizing: border-box;
  letter-spacing: 0;
}

.report-toolbar {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 12px;

  &__title {
    margin: 0;
    color: #18263d;
    font-size: 22px;
    font-weight: 700;
    line-height: 30px;
    letter-spacing: 0;
  }

  &__subtitle {
    margin-top: 4px;
    color: #758399;
    font-size: 13px;
  }

  &__actions {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    flex-wrap: wrap;
    gap: 8px;
  }

  &__site {
    width: 230px;
  }

  &__month {
    width: 142px;
  }
}

.month-option__status {
  float: right;
  margin-left: 18px;
  color: #a2adbc;
  font-size: 11px;
}

.report-state-line {
  display: flex;
  min-height: 42px;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 14px;
  border: 1px solid #dfe8f6;
  border-radius: 8px;
  background: #fff;
  padding: 8px 14px;
  color: #607089;
  font-size: 12px;

  &__main,
  &__sources {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 8px;
  }
}

.source-state {
  border-radius: 999px;
  background: #f4f6f8;
  padding: 3px 8px;
  color: #758399;
  font-weight: 600;

  &--ready {
    background: #e9fbf5;
    color: #15956a;
  }

  &--partial {
    background: #fff7e6;
    color: #c67908;
  }

  &--failed {
    background: #fff1f0;
    color: #d94b55;
  }
}

.report-print-head {
  display: none;
}

.data-line {
  display: flex;
  min-height: 40px;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 14px;
  border: 1px solid #dfe8f6;
  border-radius: 8px;
  background: #fff;
  padding: 9px 14px;
  color: #607089;
  font-size: 12px;
}

.report-empty {
  display: flex;
  min-height: 420px;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  gap: 12px;
  border: 1px solid #e6edf6;
  border-radius: 8px;
  background: #fff;
  color: #9aa6b6;

  i {
    font-size: 38px;
  }
}

@media (max-width: 1100px) {
  .report-toolbar {
    flex-direction: column;

    &__actions {
      width: 100%;
      justify-content: flex-start;
    }
  }

  .report-state-line {
    align-items: flex-start;
    flex-direction: column;
  }
}

@media (max-width: 720px) {
  .seo-effect-report-page {
    padding: 12px;
  }

  .report-toolbar__site,
  .report-toolbar__month {
    width: 100%;
  }

  .report-toolbar__actions {
    align-items: stretch;
  }

  .data-line {
    align-items: flex-start;
    flex-direction: column;
  }
}
</style>

<style lang="scss">
@media print {
  body * {
    visibility: hidden !important;
  }

  .report-print-area,
  .report-print-area * {
    visibility: visible !important;
  }

  .report-print-area {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    background: #fff;
  }

  .report-print-head {
    display: flex !important;
    align-items: flex-start;
    justify-content: space-between;
    margin-bottom: 12px;
  }

  .report-print-head h1 {
    margin: 0;
    color: #18263d;
    font-size: 22px;
  }

  .report-print-head p {
    margin: 4px 0 0;
    color: #758399;
    font-size: 13px;
  }

  .report-print-head__version {
    color: #607089;
    font-size: 13px;
  }
}
</style>
